import { useState, useEffect } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/utils/supabase";

export type AuthMode = "signin" | "signup";

interface Params {
  next: string;
  plan: string | null;
}

function readParams(): Params {
  const sp = new URLSearchParams(window.location.search);
  return {
    next: sp.get("next") || "/dashboard",
    plan: sp.get("plan"),
  };
}

function postAuthPath(p: Params): string {
  // Pro/Team go to /billing after auth (IDEAL §50).
  if (p.plan === "pro" || p.plan === "team") return "/billing";
  if (p.next === "/onboarding" && p.plan) {
    return `/onboarding?plan=${encodeURIComponent(p.plan)}`;
  }
  return p.next;
}

function confirmEmailUrl(email: string, p: Params): string {
  const sp = new URLSearchParams();
  sp.set("email", email);
  if (p.next) sp.set("next", p.next);
  if (p.plan) sp.set("plan", p.plan);
  return `${window.location.origin}/sign-up/confirm-email?${sp.toString()}`;
}

export function AuthForm({ mode }: { mode: AuthMode }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [oauthBusy, setOauthBusy] = useState<"google" | "github" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: postAuthPath(readParams()) });
    });
  }, [navigate]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const params = readParams();
    if (mode === "signin") {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      setBusy(false);
      if (error) return setError(error.message);
      navigate({ to: postAuthPath(params) });
    } else {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: confirmEmailUrl(email, params) },
      });
      setBusy(false);
      if (error) return setError(error.message);
      // If confirmations are enabled, no session is returned - show the
      // confirm-email polling page. Otherwise navigate to the resolved path.
      if (!data.session) {
        navigate({
          to: "/sign-up/confirm-email",
          search: {
            email,
            next: params.next,
            ...(params.plan ? { plan: params.plan } : {}),
          } as never,
        });
      } else {
        navigate({ to: postAuthPath(params) });
      }
    }
  }

  async function oauth(provider: "google" | "github") {
    setOauthBusy(provider);
    setError(null);
    const target = postAuthPath(readParams());
    const redirectTo = `${window.location.origin}${target}`;
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo },
    });
    if (error) {
      setOauthBusy(null);
      setError(error.message);
    }
  }

  const title = mode === "signin" ? "Welcome back" : "Create your account";
  const desc =
    mode === "signin"
      ? "Sign in to coordinate work between humans and agents."
      : "Set up an account to start tracking shared task state.";
  const submitLabel = mode === "signin" ? "Sign in" : "Create account";
  const altRoute = mode === "signin" ? "/sign-up" : "/log-in";
  const altLabel =
    mode === "signin" ? "Need an account? Sign up" : "Have an account? Log in";

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2 text-primary">
            <Sparkles className="size-5" />
            <span className="text-sm font-medium tracking-wide uppercase">
              TodoLLM
            </span>
          </div>
          <CardTitle className="text-2xl">{title}</CardTitle>
          <CardDescription>{desc}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={busy || oauthBusy !== null}
              onClick={() => oauth("google")}
            >
              <GoogleIcon />
              {oauthBusy === "google" ? "Redirecting…" : "Continue with Google"}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={busy || oauthBusy !== null}
              onClick={() => oauth("github")}
            >
              <GitHubIcon />
              {oauthBusy === "github" ? "Redirecting…" : "Continue with GitHub"}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              Google/GitHub account must have a verified email.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Separator className="flex-1" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              or
            </span>
            <Separator className="flex-1" />
          </div>

          <form className="space-y-4" onSubmit={submit}>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete={
                  mode === "signin" ? "current-password" : "new-password"
                }
              />
            </div>
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={busy || oauthBusy !== null}
            >
              {busy ? "…" : submitLabel}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex flex-col items-center gap-1">
          <Button variant="link" type="button" asChild>
            <Link to={altRoute} search={readParamsForLink()}>
              {altLabel}
            </Link>
          </Button>
          <Link
            to="/docs"
            className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            Read the docs
          </Link>
        </CardFooter>
      </Card>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" aria-hidden>
      <path
        fill="#EA4335"
        d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.7 3.5 14.6 2.5 12 2.5 6.7 2.5 2.4 6.8 2.4 12.1S6.7 21.6 12 21.6c6.9 0 9.5-4.8 9.5-7.3 0-.5-.1-.9-.1-1.3H12z"
      />
    </svg>
  );
}

function GitHubIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4 fill-current" aria-hidden>
      <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.39 7.86 10.91.58.11.79-.25.79-.55v-2.17c-3.2.7-3.87-1.36-3.87-1.36-.52-1.32-1.28-1.67-1.28-1.67-1.05-.72.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.03 1.77 2.7 1.26 3.36.96.1-.74.4-1.26.73-1.55-2.55-.29-5.24-1.28-5.24-5.7 0-1.26.45-2.29 1.18-3.1-.12-.29-.51-1.46.11-3.04 0 0 .97-.31 3.17 1.18a11 11 0 0 1 5.78 0c2.2-1.49 3.17-1.18 3.17-1.18.62 1.58.23 2.75.11 3.04.74.81 1.18 1.84 1.18 3.1 0 4.43-2.7 5.41-5.27 5.69.41.36.78 1.07.78 2.16v3.2c0 .3.21.66.79.55C20.21 21.39 23.5 17.08 23.5 12 23.5 5.65 18.35.5 12 .5z" />
    </svg>
  );
}

function readParamsForLink() {
  const p = readParams();
  const search: Record<string, string> = {};
  if (p.next && p.next !== "/dashboard") search.next = p.next;
  if (p.plan) search.plan = p.plan;
  return search;
}
